/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { waitFor } from '@ember/test-waiters';
import { BookingStatus } from 'uplisting-frontend/models/booking';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { animatedTransition } from 'uplisting-frontend/utils/router';
import { getBookingPanelBaseRouteFor } from 'uplisting-frontend/utils/rhs/routes';

interface IArgs {
  booking: ModelRegistry['booking'];
  hasUnsavedChanges?: boolean;
}

export interface BookingCancelActionsSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiBookingCancelActionsComponent extends Component<BookingCancelActionsSignature> {
  @service router!: Services['router'];
  @service notifications!: Services['notifications'];

  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];
  @service('repositories/airbnb-official-booking')
  airbnbOfficialBookingRepository!: Services['repositories/airbnb-official-booking'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @tracked showCancelModal = false;
  @tracked showCancelAirbnbModal = false;
  @tracked showDeleteModal = false;
  @tracked isAirbnbOfficialBookingCancelling = false;

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking']> {
    return this.bookingRepository.buildChangeset(this.booking);
  }

  get hasUnsavedChanges(): boolean {
    const { booking } = this;

    return (
      this.args.hasUnsavedChanges === true ||
      booking.hasDirtyAttributes ||
      !!booking.newProperty ||
      !!booking.newMultiUnit
    );
  }

  @cached
  get airbnbOfficialBookingChangeset(): GenericChangeset<
    ModelRegistry['airbnb-official-booking']
  > {
    return this.airbnbOfficialBookingRepository.buildChangeset(
      this.booking.airbnbOfficialBooking,
    );
  }

  @cached
  get reasons(): string[] {
    return this.airbnbOfficialBookingChangeset.cancellationReasons.map(
      (cancellationReason) => cancellationReason.reason,
    );
  }

  @cached
  get subReasons(): string[] {
    const reason = this.airbnbOfficialBookingChangeset.cancellationReasons.find(
      (cancellationReason) =>
        cancellationReason.reason ===
        this.airbnbOfficialBookingChangeset.reason,
    );

    return reason ? reason.sub_reasons : [];
  }

  @action
  @waitFor
  async handleCancelBooking(): Promise<void> {
    this.#discardUnsavedChanges();

    const { status } = this.booking;

    this.changeset.status = BookingStatus.cancelled;

    try {
      await this.changeset.save();
    } catch {
      this.changeset.rollback();
      this.booking.status = status;
      this.showCancelModal = false;
      this.notifications.error();

      return;
    }

    await this.#redirectAfterAction();
  }

  @action
  handleReasonSelect(reason: string): void {
    this.airbnbOfficialBookingChangeset.reason = reason;
    this.airbnbOfficialBookingChangeset.subReason = null;
  }

  @action
  handleShowCancelAirbnbModal(): void {
    this.showCancelAirbnbModal = true;
  }

  @action
  handleHideCancelAirbnbModal(): void {
    this.showCancelAirbnbModal = false;
  }

  @action
  handleShowCancelModal(): void {
    this.showCancelModal = true;
  }

  @action
  handleHideCancelModal(): void {
    this.showCancelModal = false;
  }

  @action
  handleShowDeleteModal(): void {
    this.showDeleteModal = true;
  }

  @action
  handleHideDeleteModal(): void {
    this.showDeleteModal = false;
  }

  @action
  @waitFor
  async handleCancelAirbnbBooking(): Promise<void> {
    await this.airbnbOfficialBookingChangeset.validate();

    if (this.airbnbOfficialBookingChangeset.isInvalid) {
      return;
    }

    this.airbnbOfficialBookingChangeset.status = 'cancelled_by_host';

    this.isAirbnbOfficialBookingCancelling = true;
    try {
      await this.airbnbOfficialBookingChangeset.save();

      this.notifications.info(
        'action_bookings_details.notifications.airbnb_official_booking_cancelled',
      );
    } catch {
      this.notifications.error();

      return;
    } finally {
      this.isAirbnbOfficialBookingCancelling = false;
    }

    await this.#redirectAfterAction();
  }

  @action
  @waitFor
  async handleDeleteBooking(): Promise<void> {
    const { changeover } = this.booking;

    try {
      await this.booking.destroyRecord();
    } catch {
      this.booking.rollbackAttributes();
      this.showDeleteModal = false;
      this.notifications.error();

      return;
    }

    changeover?.unloadRecord();

    await this.#redirectAfterAction();

    this.calendarManager.data.setupFasterLookup();
  }

  #discardUnsavedChanges(): void {
    this.booking.rollbackAttributes();
    this.booking.newProperty = undefined;
    this.booking.newMultiUnit = undefined;
    this.calendarManager.interaction.clearOperations();
  }

  async #redirectAfterAction(): Promise<void> {
    const baseRoute = getBookingPanelBaseRouteFor(
      this.router.currentRouteName ?? '',
    );

    animatedTransition(() => {
      this.router.transitionTo(baseRoute);
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::CancelActions': typeof UiBookingCancelActionsComponent;
  }
}
